/**
 * FilmSearchModal component for searching and adding films via TMDB
 * Features debounced search with autocomplete results
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { getApiBaseUrl } from '@/lib/api';

interface TmdbSearchResult {
  tmdbId: number;
  title: string;
  year: string;
  posterUrl: string;
  overview: string;
}

interface FilmSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddFilm: (film: {
    tmdbId: number;
    title: string;
    year: string;
  }) => Promise<void>;
  idToken: string;
}

export function FilmSearchModal({
  isOpen,
  onClose,
  onAddFilm,
  idToken,
}: FilmSearchModalProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<TmdbSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isAdding, setIsAdding] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Clear state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      setResults([]);
      setError(null);
      setIsAdding(null);
    }
  }, [isOpen]);

  // Debounced search
  const searchFilms = useCallback(
    async (searchQuery: string) => {
      if (searchQuery.length < 2) {
        setResults([]);
        return;
      }

      setIsSearching(true);
      setError(null);

      try {
        const response = await fetch(
          `${getApiBaseUrl()}/tmdb/search-multi?query=${encodeURIComponent(searchQuery)}`,
          {
            headers: {
              Authorization: `Bearer ${idToken}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error('Failed to search films');
        }

        const data = await response.json();
        setResults(data.results || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Search failed');
        setResults([]);
      } finally {
        setIsSearching(false);
      }
    },
    [idToken]
  );

  // Handle query change with debouncing
  const handleQueryChange = (value: string) => {
    setQuery(value);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      searchFilms(value);
    }, 300);
  };

  // Handle adding a film
  const handleAddFilm = async (result: TmdbSearchResult) => {
    setIsAdding(result.tmdbId);
    setError(null);

    try {
      await onAddFilm({
        tmdbId: result.tmdbId,
        title: result.title,
        year: result.year,
      });
      // Remove the added film from results
      setResults((prev) => prev.filter((r) => r.tmdbId !== result.tmdbId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add film');
    } finally {
      setIsAdding(null);
    }
  };

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-start justify-center p-4 pt-16">
        <div className="relative w-full max-w-lg transform rounded-lg bg-white shadow-xl transition-all">
          {/* Header */}
          <div className="border-b border-gray-200 px-6 py-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Add Film</h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500"
              >
                <span className="sr-only">Close</span>
                <svg
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Search input */}
          <div className="px-6 py-4">
            <div className="relative">
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => handleQueryChange(e.target.value)}
                placeholder="Search for a film..."
                className="w-full rounded-md border border-gray-300 px-4 py-2 pr-10 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
              {isSearching && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-primary" />
                </div>
              )}
            </div>

            {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
          </div>

          {/* Results */}
          <div className="max-h-96 overflow-y-auto px-6 pb-6">
            {results.length > 0 ? (
              <ul className="space-y-2">
                {results.map((result) => (
                  <li
                    key={result.tmdbId}
                    className="flex items-start gap-3 rounded-md border border-gray-200 p-3 hover:bg-gray-50"
                  >
                    {/* Poster */}
                    <div className="h-20 w-14 flex-shrink-0 overflow-hidden rounded bg-gray-100">
                      {result.posterUrl ? (
                        <img
                          src={result.posterUrl}
                          alt={result.title}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="flex h-full items-center justify-center text-xs text-gray-400">
                          No poster
                        </div>
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900">
                        {result.title}
                        {result.year && (
                          <span className="ml-1 text-gray-500">
                            ({result.year})
                          </span>
                        )}
                      </p>
                      {result.overview && (
                        <p className="mt-1 text-sm text-gray-500 line-clamp-2">
                          {result.overview}
                        </p>
                      )}
                    </div>

                    {/* Add button */}
                    <button
                      onClick={() => handleAddFilm(result)}
                      disabled={isAdding !== null}
                      className="flex-shrink-0 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
                    >
                      {isAdding === result.tmdbId ? (
                        <span className="flex items-center gap-1">
                          <div className="h-3 w-3 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                          Adding
                        </span>
                      ) : (
                        'Add'
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            ) : query.length >= 2 && !isSearching ? (
              <p className="text-center text-gray-500 py-4">
                No films found for "{query}"
              </p>
            ) : query.length === 0 ? (
              <p className="text-center text-gray-500 py-4">
                Start typing to search for films
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
